import { useEffect, useState, useRef, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import principleDeliver from "@/assets/principle-deliver.png";
import principleResponsibility from "@/assets/principle-responsibility.png";
import principleBusiness from "@/assets/principle-business.png";

interface MousePosition {
  x: number;
  y: number;
}

const PrinciplesSection = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });
  const sectionRef = useRef<HTMLElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const sectionTop = rect.top + window.scrollY;
        const relativeScroll = window.scrollY - sectionTop + window.innerHeight;
        setScrollY(relativeScroll);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const clientX = e.clientX;
    const clientY = e.clientY;
    requestAnimationFrame(() => {
      if (cardsContainerRef.current) {
        const rect = cardsContainerRef.current.getBoundingClientRect();
        const x = (clientX - rect.left - rect.width / 2) / (rect.width / 2);
        const y = (clientY - rect.top - rect.height / 2) / (rect.height / 2);
        setMousePosition({ x, y });
      }
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMousePosition({ x: 0, y: 0 });
  }, []);

  const get3DTransform = (index: number) => {
    const rotateX = -mousePosition.y * 8;
    const rotateY = mousePosition.x * 8;
    const parallaxOffset = scrollY * (0.02 + index * 0.015);
    return `
      perspective(1000px)
      translateY(${parallaxOffset}px)
      rotateX(${rotateX}deg)
      rotateY(${rotateY}deg)
    `;
  };

  const principles = [
    {
      image: principleDeliver,
      title: t("principles.principle1.title"),
      description: t("principles.principle1.description"),
      gradient: "from-emerald-500/20 to-teal-600/20",
    },
    {
      image: principleResponsibility,
      title: t("principles.principle2.title"),
      description: t("principles.principle2.description"),
      gradient: "from-blue-500/20 to-indigo-600/20",
    },
    {
      image: principleBusiness,
      title: t("principles.principle3.title"),
      description: t("principles.principle3.description"),
      gradient: "from-amber-500/20 to-orange-600/20",
    },
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-secondary/30 overflow-hidden">
      <div className="container">
        <div className="text-center mb-12">
          <span className="text-sm font-semibold uppercase tracking-wider text-primary mb-2 block">
            {t("principles.label")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t("principles.title")}
          </h2>
        </div>

        <div 
          ref={cardsContainerRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="grid md:grid-cols-3 gap-8"
          style={{ perspective: "1000px" }}
        >
          {principles.map((principle, index) => (
            <div
              key={index}
              className="group relative rounded-2xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 cursor-pointer"
              style={{
                transform: get3DTransform(index),
                transformStyle: "preserve-3d",
                transition: "transform 0.3s ease-out, box-shadow 0.3s ease-out",
              }}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={principle.image}
                  alt={principle.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                {/* Enhanced Gradient Overlays for better text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/70 to-primary/30" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-primary/90" />
              </div>

              {/* Content */}
              <div className="relative p-8 text-center h-full flex flex-col justify-end min-h-[320px] md:min-h-[380px]">
                <h3 
                  className="text-xl md:text-2xl font-bold text-white mb-4 transition-transform duration-300 group-hover:scale-105"
                  style={{
                    textShadow: "0 2px 8px rgba(0,0,0,0.6), 0 4px 16px rgba(0,0,0,0.4)",
                  }}
                >
                  {principle.title}
                </h3>
                <p 
                  className="text-sm md:text-base text-white/95 leading-relaxed font-medium"
                  style={{
                    textShadow: "0 1px 4px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)",
                  }}
                >
                  {principle.description}
                </p>
              </div>

              {/* Shine effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </div>
          ))}
        </div>

        {/* Decorative parallax elements */}
        <div
          className="absolute left-[5%] top-[30%] w-20 h-20 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 blur-2xl pointer-events-none"
          style={{
            transform: `translateY(${scrollY * -0.04}px) translateX(${mousePosition.x * 10}px)`,
          }}
        />
        <div
          className="absolute right-[10%] bottom-[20%] w-28 h-28 rounded-full bg-gradient-to-br from-emerald-500/10 to-teal-500/5 blur-2xl pointer-events-none"
          style={{
            transform: `translateY(${scrollY * -0.06}px) translateX(${mousePosition.x * -15}px)`,
          }}
        />
      </div>
    </section>
  );
};

export default PrinciplesSection;
